import type { ItemFieldRow } from './item-field-reader';
import { mergeEnchantmentArrays, readItemNumericSlot } from './enchantment-template';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot write enchantment array offsets to a scalar');
    return Object.fromEntries(Object.entries(value));
}
/** query_charges over an explicit prefetch and supplied fetchColumn result.
 * Covered stored null/false are values; an uncovered DB false alone means default.
 */
export function createChargeReader(prefetch: ItemFieldRow | null,
    fetchColumn: (holder: unknown, item: unknown) => unknown): (holder: unknown, item: unknown, fallback: unknown) => unknown {
    return (holder, item, fallback) => {
        const holderKey = phpJsonString(holder).replace(/[a-z]/g, character => character.toUpperCase());
        const covered = own(prefetch, 'covered');
        if (arrayLike(prefetch) && own(covered, holderKey) != null) {
            const rows = own(own(prefetch, 'rows'), holderKey) ?? [];
            if (!arrayLike(rows)) throw new TypeError('Charge prefetch rows must be an array');
            const itemKey = phpJsonString(item).replace(/[a-z]/g, character => character.toUpperCase());
            return clone(has(rows, itemKey) ? own(rows, itemKey) : fallback);
        }
        const result = fetchColumn(holder, item);
        return clone(result === false ? fallback : result);
    };
}
export interface EnchantmentInventoryReaders {
    buildTemplate: (id: string, includePassiveLayers: boolean) => ItemFieldRow | null;
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    layerEntries: (row: ItemFieldRow | null, name: string) => ItemFieldRow;
    hasLayer: (row: ItemFieldRow | null, name: string) => boolean;
    charges: (holder: unknown, item: unknown, fallback: unknown) => unknown;
}
/** Complete layer_enchantment_template_onto_inventory_arr. Inventory mode keeps
 * source rows and computed provenance; legacy flat mode preserves its distinct merge.
 * Charge access is read-only and requires explicit authoritative inputs.
 */
export function layerEnchantmentOntoInventory(target: ItemFieldRow, id: unknown, computeContributions: boolean,
    readers: EnchantmentInventoryReaders): ItemFieldRow {
    const out = writable(clone(target));
    if (!phpJsonTruthy(id) || !validUuid(id)) return clone(out) as ItemFieldRow;
    const data = readers.buildTemplate(id, true);
    const layers = own(data, 'layers_to_add') ?? [];
    const materialize = (key: string, value: unknown): void => {
        const bucket = writable(out.item_layer_types); bucket[key] = value; out.item_layer_types = bucket;
    };
    if (computeContributions) {
        const template = writable(clone(own(data, 'enchantment_templates') ?? []));
        for (const key of ['enchantment_template_private', 'enchantment_template_deleted', 'enchantment_template_name']) {
            if (!phpJsonTruthy(own(template, key))) delete template[key];
        }
        out.enchantment_templates = template;
        out.enchantment_template_enchantments = clone(own(data, 'enchantment_template_enchantments') ?? []);
        const enchantment: Record<string, unknown> = { enchantment_template_id: id,
            item_name: readers.layerValue(data, 'item_name') ?? '',
            total_cost: readItemNumericSlot(readers.layerValue(data, 'total_cost') ?? 0),
            abilities: clone(own(data, 'nested_abilities') ?? []) };
        const multipliers: number[] = [];
        for (const entry of Object.values(own(data, 'cost_multipliers') ?? [])) {
            const value = readItemNumericSlot(entry, 1);
            if (value !== 0 && value !== 1) multipliers.push(value);
        }
        if (multipliers.length) enchantment.cost_multipliers = multipliers;
        if (readers.layerValue(data, 'item_charges') !== null) enchantment.item_charges = readItemNumericSlot(readers.layerValue(data, 'item_charges'));
        if (readItemNumericSlot(readers.layerValue(data, 'item_rechargeable') ?? 0)) enchantment.item_rechargeable = 1;
        const computed = writable(out.computed); computed.enchantment = enchantment; out.computed = computed;
    } else {
        for (const [type, values] of Object.entries(layers)) {
            if (!arrayLike(values)) continue;
            const previous = own(out, type) ?? [];
            if (!arrayLike(previous)) throw new TypeError('Legacy enchantment merge requires an array');
            Object.defineProperty(out, type, { value: mergeEnchantmentArrays(previous, values), writable: true, enumerable: true, configurable: true });
        }
    }
    if (!readers.hasLayer(out, 'cost_gp')) out.cost_gp = [0];
    let cost = readItemNumericSlot(readers.layerValue(out, 'cost_gp') ?? 0) + readItemNumericSlot(readers.layerValue(data, 'total_cost') ?? 0);
    for (const entry of Object.values(readers.layerEntries(data, 'cost_multipliers'))) {
        const multiplier = readItemNumericSlot(entry, 1); if (multiplier !== 0) cost *= multiplier;
    }
    out.cost_gp = [cost];
    if (readers.layerValue(out, 'weight') !== null && readers.layerValue(out, 'weight') !== '' && readers.layerValue(out, 'weight') !== null) {
        let weight = readItemNumericSlot(readers.layerValue(out, 'weight'));
        if (weight !== 0) {
            for (const entry of Object.values(readers.layerEntries(data, 'weight_multipliers'))) {
                const multiplier = readItemNumericSlot(entry, 1); if (multiplier !== 0) weight *= multiplier;
            }
            materialize('weight', weight);
        }
    }
    if (readers.layerValue(data, 'item_charges') !== null) materialize('item_charges', readItemNumericSlot(readers.layerValue(data, 'item_charges')));
    if (readInventoryField(out, 'inventory_id') !== null && readers.layerValue(out, 'item_charges') !== null) {
        materialize('current_item_charges', readers.charges(readInventoryField(out, 'inventory_id'), readInventoryField(out, 'inventory_id'), readers.layerValue(out, 'item_charges')));
    }
    if (phpJsonTruthy(readers.layerValue(data, 'item_rechargeable'))) materialize('item_rechargeable', 1);
    return clone(out) as ItemFieldRow;
}
